import React, { useState, useEffect } from "react";
import { Editor, Frame } from "@craftjs/core";
import {
  Container,
  Text,
  Button,
  Image,
  HeroBanner,
  ProductShowcase,
  FeaturesList,
  PromoBanner,
  TestimonialsSection,
  ContactSection,
  FooterSection,
  Divider,
} from "../custom-builder/CraftComponents";
import { CRAFT_DEFAULT_STATE } from "../custom-builder/CraftDefaultTemplate";
import { CraftStoreContext } from "../custom-builder/CraftStoreContext";
import { storeSettingsService } from "../../../services/storeSettings.service";
import {
  MdShoppingCart,
  MdSearch,
  MdMenu,
  MdStorefront,
} from "react-icons/md";

const CRAFT_RESOLVER = {
  Container,
  Text,
  Button,
  Image,
  HeroBanner,
  ProductShowcase,
  FeaturesList,
  PromoBanner,
  TestimonialsSection,
  ContactSection,
  FooterSection,
  Divider,
};

export default function CustomCraftLayout({ p }) {
  const theme = p.storeTheme || {};
  const [craftDataJson, setCraftDataJson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const cartCount =
    p.cart?.reduce((sum, item) => sum + (item.cartQuantity || 1), 0) || 0;

  useEffect(() => {
    let isMounted = true;
    const fetchActiveLayout = async () => {
      try {
        if (p.bussinessUuid) {
          const res = await storeSettingsService.getActiveCustomThemeLayout(
            p.bussinessUuid
          );
          if (isMounted && res?.success && res.data) {
            if (res.data.serializedData) {
              setCraftDataJson(res.data.serializedData);
            } else if (res.data.craftData) {
              setCraftDataJson(JSON.stringify(res.data.craftData));
            }
          }
        }
      } catch (err) {
        console.warn("Failed to fetch active custom layout, using default template", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActiveLayout();

    return () => {
      isMounted = false;
    };
  }, [p.bussinessUuid]);

  const jsonToRender = craftDataJson || JSON.stringify(CRAFT_DEFAULT_STATE);

  const liveProducts =
    p.filteredProducts?.length > 0
      ? p.filteredProducts
      : p.allProducts || [];

  return (
    <CraftStoreContext.Provider
      value={{
        products: liveProducts,
        onAddToCart: p.handleAddToCart,
        bussinessUuid: p.bussinessUuid,
        displayBusinessName:
          p.displayBusinessName || p.storeInfo?.businessName || "",
        previewMode: Boolean(p.previewMode),
        isLiveStorefront: true,
      }}
    >
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 font-sans flex flex-col overflow-x-hidden w-full">
        {/* Top Navbar */}
        <header className="sticky top-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
            {/* Mobile Menu Toggle & Brand */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-700 dark:text-gray-300 sm:hidden rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
              >
                <MdMenu size={22} />
              </button>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center shadow-md flex-shrink-0">
                  <MdStorefront size={20} />
                </div>
                <span className="font-extrabold text-lg sm:text-xl text-gray-900 dark:text-white tracking-tight truncate max-w-[140px] sm:max-w-xs">
                  {p.storeInfo?.businessName || theme.themeName || "Online Store"}
                </span>
              </div>
            </div>

            {/* Search bar */}
            <div className="hidden sm:flex flex-1 max-w-md mx-4">
              <div className="w-full relative">
                <MdSearch
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={p.searchQuery || ""}
                  onChange={(e) => p.setSearchQuery?.(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border-none rounded-full text-xs sm:text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
            </div>

            {/* User Menu & Cart */}
            <div className="flex items-center gap-3">
              {p.userMenuElement}

              <button
                type="button"
                onClick={() => p.handleOpenCart?.()}
                className="relative p-2.5 rounded-full bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 transition-colors shadow-sm"
                title="Shopping Cart"
              >
                <MdShoppingCart size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center shadow">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        {mobileMenuOpen && (
          <div className="sm:hidden absolute top-16 left-0 right-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 shadow-lg z-30 animate-fadeIn">
            <div className="w-full relative">
              <MdSearch size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={p.searchQuery || ""}
                onChange={(e) => p.setSearchQuery?.(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 dark:bg-gray-800 border-none rounded-xl text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>
        )}

        {/* Main Craft.js Custom Page Canvas */}
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Editor resolver={CRAFT_RESOLVER} enabled={false}>
              <Frame
                key={craftDataJson ? "custom-saved" : "default"}
                data={jsonToRender}
              />
            </Editor>
          )}
        </main>
      </div>
    </CraftStoreContext.Provider>
  );
}

